import React, { useEffect, useState } from "react";
import { Alert, Box, Button, InputAdornment, Paper, Snackbar, Stack, Switch, TextField, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PlaceIcon from "@mui/icons-material/Place";
import AddIcon from "@mui/icons-material/Add";
import BadgeIcon from "@mui/icons-material/Badge";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectWorkshopDetails } from "../../../app/store/userSlice";
import { useUpdateWorkshopMutation } from "../../../app/store/api/workshopApi";
import toast from "react-hot-toast";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Stable ids so React doesn't mix up rows when one is removed
let rowId = 0;
const newRow = (number = "", extraInfo = "") => ({ id: ++rowId, number, extraInfo });

const buildInitialState = (details) => ({
  active: details?.active || false,
  description: details?.description || "",
  extraDescription: details?.extraDescription || "",
  email: details?.email || "",
  address: details?.address || "",
  wwwPages: details?.wwwPages || "",
  phones: Array.isArray(details?.phone) && details.phone.length > 0 ? details.phone.map((p) => newRow(p.number, p.extraInfo)) : [newRow()],
});

// Normalizes the form (trim, drop empty phone rows, strip local ids).
// Used both for the save payload and for detecting unsaved changes.
const toPayload = (form) => ({
  active: form.active,
  description: form.description.trim(),
  extraDescription: form.extraDescription.trim(),
  phone: form.phones.filter((p) => p.number.trim() !== "").map(({ number, extraInfo }) => ({ number: number.trim(), extraInfo: extraInfo.trim() })),
  email: form.email.trim(),
  wwwPages: form.wwwPages.trim(),
  // address: form.address.trim(),
});

const labelSx = { fontWeight: 600, color: "text.primary", lineHeight: 1.3 };
const iconSx = { color: "#8a94a6" };
const outlinedBtnSx = { borderColor: "#e3e6ec", color: "#003C59", height: 55, whiteSpace: "nowrap" };

const WorkshopInfo = () => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const workshopDetails = useSelector(selectWorkshopDetails);

  const [form, setForm] = useState(() => buildInitialState(workshopDetails));
  // Snapshot of the last loaded/saved values, used to detect unsaved changes
  const [baseline, setBaseline] = useState(() => JSON.stringify(toPayload(buildInitialState(workshopDetails))));
  const [touched, setTouched] = useState({});
  const [updateWorkshop, { isLoading: saving }] = useUpdateWorkshopMutation();

  // Re-sync the form when details arrive/change in the store (e.g. loaded after mount)
  useEffect(() => {
    const initial = buildInitialState(workshopDetails);
    setForm(initial);
    setBaseline(JSON.stringify(toPayload(initial)));
    setTouched({});
  }, [workshopDetails]);

  const isDirty = JSON.stringify(toPayload(form)) !== baseline;

  const setField = (field) => (e) => {
    const value = field === "active" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const markTouched = (field) => () => setTouched((prev) => ({ ...prev, [field]: true }));

  // ---- phone rows ----
  const addPhone = () => setForm((prev) => ({ ...prev, phones: [...prev.phones, newRow()] }));

  const removePhone = (id) => setForm((prev) => ({ ...prev, phones: prev.phones.filter((p) => p.id !== id) }));

  const updatePhone = (id, key, value) =>
    setForm((prev) => ({
      ...prev,
      phones: prev.phones.map((p) => (p.id === id ? { ...p, [key]: value } : p)),
    }));

  // ---- validation ----
  const emailError = form.email.trim() !== "" && !EMAIL_REGEX.test(form.email.trim());
  const showEmailError = touched.email && emailError;

  // ---- save ----
  const handleSave = async () => {
    setTouched((prev) => ({ ...prev, email: true }));
    if (emailError) return;

    const payload = toPayload(form);

    toast.promise(
      async () => {
        await updateWorkshop({ workshopId: workshopDetails.id, payload }).unwrap();
      },
      {
        success: t("toastMessage.saveSuccess"),
        loading: t("toastMessage.saveLoading"),
        error: t("toastMessage.saveError"),
      },
    );
  };

  return (
    <Paper variant='outlined' sx={{ borderColor: "#e3e6ec", borderRadius: 2, p: 3, mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 700, color: "#003C59", lineHeight: 1.3 }}>
          {t("workshopInfo.workshopInformation")}
        </Typography>
      </Box>

      {/* Description */}
      <Box sx={{ mb: 1 }}>
        <Typography variant='body2' sx={labelSx}>
          {t("workshopInfo.description")}
        </Typography>
      </Box>
      <TextField fullWidth multiline minRows={2} value={form.description} onChange={setField("description")} sx={{ mb: 1 }} />

      {/* Extra description */}
      <Box sx={{ mb: 1 }}>
        <Typography variant='body2' sx={labelSx}>
          {t("workshopInfo.extraDescription")}
        </Typography>
      </Box>
      <TextField fullWidth multiline minRows={2} value={form.extraDescription} onChange={setField("extraDescription")} sx={{ mb: 0.5 }} />
      <Typography variant='caption' sx={{ color: "#8a94a6", display: "block", mb: 3 }}>
        {t("workshopInfo.extraDescriptionNote")}
      </Typography>

      {/* Phone numbers */}
      {form.phones.map((item, index) => (
        <Stack key={item.id} direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ height: "auto", mb: 1, justifyContent: "space-between", flexWrap: "wrap", alignItems: "center" }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 1 }}>
              <Typography variant='body2' sx={labelSx}>
                {t("workshopInfo.phoneNumber")}
              </Typography>
            </Box>
            <TextField
              fullWidth
              type='tel'
              placeholder='+358 40 123 4567'
              value={item.number}
              onChange={(e) => updatePhone(item.id, "number", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <PhoneIcon fontSize='small' sx={iconSx} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 1 }}>
              <Typography variant='body2' sx={labelSx}>
                {t("workshopInfo.name")}
              </Typography>
            </Box>
            <TextField
              fullWidth
              placeholder='John Doe'
              value={item.extraInfo}
              onChange={(e) => updatePhone(item.id, "extraInfo", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <BadgeIcon fontSize='small' sx={iconSx} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-end", pt: !isMobile && 3, gap: 1, width: isMobile ? "100%" : "auto" }}>
            {index === form.phones.length - 1 ? (
              <Button variant='outlined' startIcon={<AddIcon fontSize='small' />} onClick={addPhone} sx={outlinedBtnSx} fullWidth={isMobile}>
                {t("workshopSettings.add")}
              </Button>
            ) : (
              <Button variant='outlined' startIcon={<DeleteIcon fontSize='small' />} onClick={() => removePhone(item.id)} sx={outlinedBtnSx} fullWidth={isMobile}>
                {t("workshopSettings.remove")}
              </Button>
            )}
          </Box>
        </Stack>
      ))}

      {/* Email */}
      <Box sx={{ flex: 1, mb: 1 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant='body2' sx={labelSx}>
            {t("workshopInfo.email")}
          </Typography>
        </Box>
        <TextField
          fullWidth
          type='email'
          placeholder='info@korjaamo.fi'
          value={form.email}
          onChange={setField("email")}
          onBlur={markTouched("email")}
          error={showEmailError}
          helperText={showEmailError ? t("workshopInfo.invalidEmail", "Enter a valid email address") : ""}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <EmailIcon fontSize='small' sx={iconSx} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Address */}
      <Box sx={{ mb: 1 }}>
        <Typography variant='body2' sx={labelSx}>
          {t("workshopInfo.address")}
        </Typography>
      </Box>
      <TextField
        fullWidth
        placeholder='Esimerkkikatu 12, 00100 Helsinki'
        value={form.address}
        disabled={true}
        onChange={setField("address")}
        sx={{ mb: 1, cursor: "not-allowed" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <PlaceIcon fontSize='small' sx={iconSx} />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ mb: 1 }}>
        <Typography variant='body2' sx={labelSx}>
          {t("workshopInfo.webSiteUrl")}
        </Typography>
      </Box>
      <TextField
        fullWidth
        placeholder='https://www.korjaamo.fi'
        value={form.wwwPages}
        onChange={setField("wwwPages")}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <PlaceIcon fontSize='small' sx={iconSx} />
            </InputAdornment>
          ),
        }}
      />

      <Button variant='contained' startIcon={<SaveIcon fontSize='small' />} onClick={handleSave} disabled={saving || !isDirty} sx={{ bgcolor: "#003C59", "&:hover": { bgcolor: "#003C59" } }}>
        {t("workshopSettings.save")}
      </Button>
    </Paper>
  );
};

export default WorkshopInfo;
